// Sales Analysis Table - Tableau récapitulatif COMPLET pour le closing
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  TrendingUp, TrendingDown, AlertTriangle, CheckCircle, 
  Target, DollarSign, Users, Utensils, Clock, Instagram,
  ChevronDown, ChevronUp, Zap, Lightbulb, Flame, AlertCircle,
  Calendar, MessageCircle, Phone, Brain, Shield, Rocket,
  Star, Award, ArrowRight, X, HelpCircle
} from 'lucide-react';

const theme = {
  orange: '#E8621A',
  green: '#1D7A4F',
  red: '#C0392B',
  amber: '#F59E0B',
  blue: '#1A4A8A',
  purple: '#7C3AED',
};

// Analyse complète des données du lead pour générer le tableau de vente
const analyzeLead = (lead) => {
  const diag = lead.diag || {};
  const answers = lead.all_answers || {};
  
  // Parse values
  const parseNum = (val) => {
    if (!val || val === 'Inconnu') return null;
    const num = parseInt(String(val).replace(/[^0-9-]/g, ''));
    return isNaN(num) ? null : num;
  };
  
  const ca = parseNum(diag.ca);
  const fc = parseNum(diag.fc);
  const ms = parseNum(diag.ms);
  const ebe = parseNum(diag.ebe);
  const primeCost = parseNum(diag.prime_cost);
  const loyer = parseNum(diag.loyer);
  const ticketMoyen = parseNum(diag.ticket_moyen) || answers.ticket_moyen || 15;
  
  const analysis = {
    problems: [],
    opportunities: [],
    actionPlan: [],
    priorities: [],
    totalPotentialGain: 0,
    totalMonthlyLoss: 0,
    urgencyLevel: 'medium',
    mainSalesAngle: '',
    punchlines: [],
    closingAngles: [],
    objections: [],
    aiRecommendations: [],
    primeCostData: null,
    maturityScore: 50
  };
  
  // ====== CALCUL PRIME COST ======
  if (fc !== null || ms !== null) {
    const fcVal = fc || 30;
    const msVal = ms || 30;
    const total = fcVal + msVal;
    analysis.primeCostData = {
      foodCost: fcVal,
      masseSalariale: msVal,
      total: total,
      target: 60,
      isWarning: total > 65,
      isCritical: total > 70
    };
  }
  
  // ====== ANALYSE DES PROBLÈMES ======
  
  // 1. Food Cost
  if (fc !== null) {
    if (fc > 35) {
      const loss = ca ? Math.round(ca * (fc - 30) / 100) : 3000;
      const actionGain = ca ? Math.round(ca * 0.05) : 2500;
      analysis.problems.push({
        id: 'fc_critical',
        category: 'Finance',
        icon: Utensils,
        label: 'Food Cost CRITIQUE',
        value: `${fc}%`,
        target: '< 30%',
        severity: 'critical',
        monthlyLoss: loss,
        punchline: `Tu perds ${loss.toLocaleString('fr-FR')}€/mois juste sur les matières premières`,
        actions: ['Fiches techniques + grammages', 'Renégo fournisseurs', 'Supprimer plats non rentables']
      });
      analysis.actionPlan.push({
        icon: '🍽️',
        label: 'Réduction food cost',
        detail: `-${fc - 30}%`,
        gain: actionGain,
        priority: 2
      });
      analysis.totalPotentialGain += actionGain;
      analysis.totalMonthlyLoss += loss;
      analysis.urgencyLevel = 'critical';
    } else if (fc > 30) {
      const loss = ca ? Math.round(ca * (fc - 30) / 100) : 1500;
      const actionGain = ca ? Math.round(ca * 0.03) : 1500;
      analysis.problems.push({
        id: 'fc_high',
        category: 'Finance',
        icon: Utensils,
        label: 'Food Cost Élevé',
        value: `${fc}%`,
        target: '< 30%',
        severity: 'high',
        monthlyLoss: loss,
        punchline: `${loss.toLocaleString('fr-FR')}€/mois qui s'évaporent sans que tu le voies`,
        actions: ['Contrôle des portions', 'Inventaire hebdo', 'Optimiser la carte']
      });
      analysis.actionPlan.push({
        icon: '🍽️',
        label: 'Réduction food cost',
        detail: `-${fc - 30}%`,
        gain: actionGain,
        priority: 2
      });
      analysis.totalPotentialGain += actionGain;
      analysis.totalMonthlyLoss += loss;
    }
  } else if (diag.fc === 'Inconnu') {
    const estimatedLoss = ca ? Math.round(ca * 0.05) : 2000;
    analysis.problems.push({
      id: 'fc_unknown',
      category: 'Pilotage',
      icon: AlertTriangle,
      label: 'Food Cost INCONNU',
      value: '???',
      target: 'À mesurer',
      severity: 'critical',
      monthlyLoss: estimatedLoss,
      punchline: `Tu pilotes à l'aveugle. 99% des restos qui ne connaissent pas leur FC perdent de l'argent`,
      actions: ['Mettre en place le suivi FC', 'Former l\'équipe', 'Tableaux de bord']
    });
    analysis.actionPlan.push({
      icon: '📊',
      label: 'Mise en place pilotage',
      detail: 'FC + KPIs',
      gain: estimatedLoss,
      priority: 1
    });
    analysis.totalPotentialGain += estimatedLoss;
    analysis.totalMonthlyLoss += estimatedLoss;
    analysis.urgencyLevel = 'critical';
  }
  
  // 2. Masse Salariale
  if (ms !== null && ms > 35) {
    const excess = ms - 35;
    const loss = ca ? Math.round(ca * excess / 100 * 0.7) : 1500;
    const actionGain = ca ? Math.round(ca * 0.03) : 1500;
    analysis.problems.push({
      id: 'ms_high',
      category: 'RH',
      icon: Users,
      label: 'Masse Salariale Élevée',
      value: `${ms}%`,
      target: '< 35%',
      severity: ms > 40 ? 'critical' : 'high',
      monthlyLoss: loss,
      punchline: `Tu paies des heures non productives tous les jours`,
      actions: ['Optimiser le planning', 'Polyvalence équipe', 'Primes sur résultats']
    });
    analysis.actionPlan.push({
      icon: '👥',
      label: 'Optimisation masse salariale',
      detail: `-${excess}%`,
      gain: actionGain,
      priority: 3
    });
    analysis.totalPotentialGain += actionGain;
    analysis.totalMonthlyLoss += loss;
    if (ms > 40) analysis.urgencyLevel = 'critical';
  }
  
  // 3. Prime Cost
  if (analysis.primeCostData && analysis.primeCostData.total > 65) {
    if (!analysis.problems.find(p => p.id === 'prime_cost')) {
      analysis.problems.push({
        id: 'prime_cost',
        category: 'Finance',
        icon: DollarSign,
        label: 'Prime Cost Dangereux',
        value: `${analysis.primeCostData.total}%`,
        target: '< 65%',
        severity: analysis.primeCostData.total > 70 ? 'critical' : 'high',
        monthlyLoss: 0,
        punchline: `Avec un prime cost à ${analysis.primeCostData.total}%, il te reste presque rien après charges`,
        actions: ['Audit complet FC + MS', 'Restructurer les coûts', 'Revoir le pricing']
      });
    }
    if (analysis.primeCostData.total > 70) analysis.urgencyLevel = 'critical';
  }
  
  // 4. EBE négatif ou faible
  if (ebe !== null) {
    if (ebe < 0) {
      analysis.problems.push({
        id: 'ebe_negative',
        category: 'Rentabilité',
        icon: TrendingDown,
        label: 'EBE NÉGATIF',
        value: `${ebe.toLocaleString('fr-FR')}€`,
        target: '> 10% CA',
        severity: 'critical',
        monthlyLoss: Math.abs(ebe),
        punchline: `Tu perds ${Math.abs(ebe).toLocaleString('fr-FR')}€ par mois. Urgence absolue.`,
        actions: ['Stopper l\'hémorragie', 'Restructuration immédiate', 'Plan de redressement']
      });
      analysis.totalMonthlyLoss += Math.abs(ebe);
      analysis.urgencyLevel = 'critical';
    } else if (ca && (ebe / ca) < 0.05) {
      const potentialGain = ca ? Math.round(ca * 0.05) - ebe : 2000;
      analysis.problems.push({
        id: 'ebe_low',
        category: 'Rentabilité',
        icon: TrendingDown,
        label: 'Marge Très Faible',
        value: `${ebe.toLocaleString('fr-FR')}€`,
        target: '> 10% CA',
        severity: 'high',
        monthlyLoss: potentialGain,
        punchline: `Tu travailles beaucoup pour gagner très peu`,
        actions: ['Augmenter le ticket moyen', 'Réduire les charges', 'Optimiser']
      });
      analysis.totalMonthlyLoss += potentialGain;
    }
  }
  
  // 5. Autonomie
  if (answers.autonomie === 'pas_1_jour') {
    analysis.problems.push({
      id: 'autonomie',
      category: 'Organisation',
      icon: Clock,
      label: 'Dépendance Totale',
      value: 'Critique',
      target: 'Autonome',
      severity: 'high',
      monthlyLoss: 0,
      punchline: `Tu es esclave de ton resto. Si tu tombes malade, tout s'arrête.`,
      actions: ['Documenter les process', 'Former un bras droit', 'Déléguer progressivement']
    });
  }
  
  // 6. Pas de présence digitale
  if (answers.freq_insta === '0x/semaine' || answers.instagram_freq === 0) {
    const digitalGain = ca ? Math.round(ca * 0.07) : 3000;
    analysis.problems.push({
      id: 'digital_absent',
      category: 'Marketing',
      icon: Instagram,
      label: 'Zéro Présence Digitale',
      value: '0 post',
      target: '3-5x/sem',
      severity: 'medium',
      monthlyLoss: ca ? Math.round(ca * 0.05) : 1500,
      punchline: `Tes concurrents récupèrent tes clients potentiels sur Instagram`,
      actions: ['Créer un calendrier éditorial', 'Stories quotidiennes', 'Reels tendances']
    });
    analysis.actionPlan.push({
      icon: '🚀',
      label: 'Acquisition clients',
      detail: 'Ads + contenu',
      gain: digitalGain,
      priority: 4
    });
    analysis.totalPotentialGain += digitalGain;
    analysis.totalMonthlyLoss += ca ? Math.round(ca * 0.05) : 1500;
  }
  
  // ====== OPPORTUNITÉS DE CROISSANCE ======
  
  // Ticket moyen
  if (ticketMoyen && ticketMoyen < 20) {
    const gainTicket = ca ? Math.round((ca / ticketMoyen) * 2 / 30 * 30) : 4000;
    analysis.opportunities.push({
      id: 'ticket_moyen',
      label: 'Ticket moyen à optimiser',
      potential: gainTicket,
      description: `+2€ par client = ${gainTicket.toLocaleString('fr-FR')}€/mois sans plus de clients`
    });
    analysis.actionPlan.push({
      icon: '📈',
      label: 'Optimisation ticket moyen',
      detail: '+2€',
      gain: gainTicket,
      priority: 1
    });
    analysis.totalPotentialGain += gainTicket;
  }
  
  // CA faible = potentiel de croissance
  if (ca && ca < 40000) {
    const growthGain = Math.round(ca * 0.15);
    analysis.opportunities.push({
      id: 'growth_ca',
      label: 'Potentiel CA non exploité',
      potential: growthGain,
      description: 'Avec les bonnes actions, +15-25% de CA est réaliste en 90 jours'
    });
    if (!analysis.actionPlan.find(a => a.label.includes('Acquisition'))) {
      analysis.actionPlan.push({
        icon: '🚀',
        label: 'Acquisition clients',
        detail: 'Ads + contenu',
        gain: growthGain,
        priority: 4
      });
      analysis.totalPotentialGain += growthGain;
    }
  }
  
  // ====== TRIER PLAN D'ACTION PAR PRIORITÉ ======
  analysis.actionPlan.sort((a, b) => a.priority - b.priority);
  analysis.priorities = analysis.actionPlan.map((a, idx) => ({
    rank: idx + 1,
    label: a.label,
    type: idx === 0 ? 'quick win' : idx < 3 ? 'high impact' : 'growth'
  }));
  
  // ====== ANGLES DE CLOSING ======
  analysis.closingAngles = [
    `Tu perds déjà ${analysis.totalMonthlyLoss.toLocaleString('fr-FR')}€/mois → le problème est réel`,
    ca && ca >= 40000 ? 'Ton resto est stable → parfait pour scaler' : 'Ton resto a du potentiel → c\'est le moment d\'agir',
    'On ne parle pas de théorie → mais d\'actions directes',
    'ROI rapide (30–90 jours)'
  ];
  
  // ====== OBJECTIONS ANTICIPÉES ======
  analysis.objections = [
    {
      objection: "J'ai pas le temps",
      response: "C'est justement pour ça qu'on est là. On te donne un plan d'action clair et on te suit. Tu gagnes du temps, pas l'inverse."
    },
    {
      objection: "C'est cher",
      response: `On parle de ${analysis.totalPotentialGain.toLocaleString('fr-FR')}€/mois de gains potentiels. Le programme se rembourse le 1er mois.`
    },
    {
      objection: "Je gère déjà bien",
      response: `Tu perds ${analysis.totalMonthlyLoss.toLocaleString('fr-FR')}€/mois sans le savoir. "Bien gérer" c'est optimiser chaque euro.`
    },
    {
      objection: "J'ai déjà essayé des formations",
      response: "On ne fait pas de la formation. On implémente avec toi, on suit les résultats. C'est du coaching opérationnel."
    }
  ];
  
  // ====== RECOMMANDATIONS IA ======
  if (fc && fc > 30) {
    analysis.aiRecommendations.push({
      icon: '🍽️',
      text: `Augmenter prix carte de +${Math.round((fc - 28) / 2)}% pour absorber le food cost`
    });
    analysis.aiRecommendations.push({
      icon: '📉',
      text: 'Réduire grammages des accompagnements de 10%'
    });
  }
  if (ticketMoyen && ticketMoyen < 18) {
    analysis.aiRecommendations.push({
      icon: '💰',
      text: 'Créer menu "signature" à +5€ du ticket moyen actuel'
    });
  }
  if (answers.freq_insta === '0x/semaine') {
    analysis.aiRecommendations.push({
      icon: '📱',
      text: 'Lancer campagne Instagram Reels (3 posts/semaine minimum)'
    });
  }
  if (ms && ms > 35) {
    analysis.aiRecommendations.push({
      icon: '👥',
      text: 'Restructurer planning avec polyvalence (économie 10-15%)'
    });
  }
  if (analysis.aiRecommendations.length === 0) {
    analysis.aiRecommendations.push({
      icon: '🎯',
      text: 'Focus sur l\'acquisition client et l\'optimisation digitale'
    });
  }
  
  // ====== SCORE DE MATURITÉ ======
  let score = 50;
  if (fc && fc <= 30) score += 10;
  if (fc && fc > 35) score -= 15;
  if (ms && ms <= 35) score += 10;
  if (ms && ms > 40) score -= 15;
  if (ebe && ebe > 0 && ca && (ebe / ca) > 0.08) score += 15;
  if (ebe && ebe < 0) score -= 25;
  if (answers.autonomie === 'ok') score += 10;
  if (answers.autonomie === 'pas_1_jour') score -= 10;
  if (ca && ca > 50000) score += 10;
  analysis.maturityScore = Math.max(0, Math.min(100, score));
  
  // ====== ANGLE DE VENTE PRINCIPAL ======
  if (analysis.problems.length > 0) {
    const mainProblem = analysis.problems.sort((a, b) => (b.monthlyLoss || 0) - (a.monthlyLoss || 0))[0];
    analysis.mainSalesAngle = mainProblem.punchline;
    analysis.punchlines = [
      `Tu laisses ${analysis.totalMonthlyLoss.toLocaleString('fr-FR')}€ sur la table chaque mois`,
      mainProblem.punchline,
      `En 90 jours, on peut récupérer +${analysis.totalPotentialGain.toLocaleString('fr-FR')}€/mois`
    ];
  } else {
    analysis.mainSalesAngle = 'Ton resto est bien piloté. Focus sur la croissance et le scale.';
    analysis.punchlines = [
      'Ton resto a de bonnes bases',
      'On peut maintenant accélérer la croissance',
      'Prêt à passer au niveau supérieur ?'
    ];
  }
  
  return analysis;
};

// Composant Badge de sévérité
const SeverityBadge = ({ severity }) => {
  const config = {
    critical: { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-300', label: 'CRITIQUE' },
    high: { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-300', label: 'ÉLEVÉ' },
    medium: { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-300', label: 'MOYEN' },
    low: { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-300', label: 'FAIBLE' },
  };
  const c = config[severity] || config.medium;
  
  return (
    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${c.bg} ${c.text} border ${c.border}`}>
      {c.label}
    </span>
  );
};

// Score de maturité visuel
const MaturityScore = ({ score }) => {
  const getLevel = () => {
    if (score < 40) return { label: 'DANGER', color: 'text-red-600', bg: 'bg-red-500' };
    if (score < 70) return { label: 'OPTIMISATION', color: 'text-amber-600', bg: 'bg-amber-500' };
    return { label: 'SCALING', color: 'text-green-600', bg: 'bg-green-500' };
  };
  const level = getLevel();
  
  return (
    <div className="p-4 rounded-xl bg-gradient-to-br from-gray-900 to-gray-800">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">📊 SCORE RESTO</span>
        <span className={`text-3xl font-black ${level.color}`}>{score}/100</span>
      </div>
      <div className="w-full h-3 bg-gray-700 rounded-full overflow-hidden mb-2">
        <div 
          className={`h-full ${level.bg} transition-all duration-500`}
          style={{ width: `${score}%` }}
        />
      </div>
      <div className="flex justify-between text-[10px] text-gray-500">
        <span>0-40 Danger</span>
        <span>40-70 Optimisation</span>
        <span>70+ Scaling</span>
      </div>
      <div className={`mt-2 text-center font-bold ${level.color}`}>
        → {level.label}
      </div>
    </div>
  );
};

// Composant principal : Tableau d'analyse des ventes
const SalesAnalysisTable = ({ lead, onUpdateField }) => {
  const [expandedRow, setExpandedRow] = useState(null);
  const [showObjections, setShowObjections] = useState(false);
  const analysis = analyzeLead(lead);
  
  // Psychologie du lead state
  const [psychology, setPsychology] = useState({
    douleur: lead.psychology?.douleur || '',
    objectif: lead.psychology?.objectif || '',
    urgence: lead.psychology?.urgence || 'medium',
    budget: lead.psychology?.budget || '',
    motivation: lead.psychology?.motivation || ''
  });
  
  const handlePsychologyChange = (field, value) => {
    const newPsych = { ...psychology, [field]: value };
    setPsychology(newPsych);
    if (onUpdateField) {
      onUpdateField(lead.id, 'psychology', newPsych);
    }
  };
  
  return (
    <div className="space-y-4">
      {/* 1. HEADER - ARGENT PERDU + IMPACT 12 MOIS */}
      <div className="p-4 rounded-xl bg-gradient-to-r from-red-600 to-red-500 text-white">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-bold opacity-90">💸 ARGENT PERDU CHAQUE MOIS</span>
          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
            analysis.urgencyLevel === 'critical' ? 'bg-white text-red-600 animate-pulse' : 'bg-white/20 text-white'
          }`}>
            {analysis.urgencyLevel === 'critical' ? '🚨 URGENT' : '⚠️ À TRAITER'}
          </span>
        </div>
        <p className="text-4xl font-black">
          {analysis.totalMonthlyLoss.toLocaleString('fr-FR')}€
        </p>
        <div className="mt-2 p-2 rounded-lg bg-white/10">
          <p className="text-sm font-bold">
            ⏳ IMPACT SUR 12 MOIS : <span className="text-yellow-300">{(analysis.totalMonthlyLoss * 12).toLocaleString('fr-FR')}€</span> perdus
          </p>
        </div>
        <p className="text-xs mt-2 opacity-80 font-bold">
          ⚠️ Chaque mois perdu = -{analysis.totalMonthlyLoss.toLocaleString('fr-FR')}€
        </p>
      </div>
      
      {/* 2. PRIME COST (TRÈS IMPORTANT) */}
      {analysis.primeCostData && (
        <div className={`p-4 rounded-xl border-2 ${
          analysis.primeCostData.isCritical ? 'border-red-400 bg-red-50' : 
          analysis.primeCostData.isWarning ? 'border-orange-400 bg-orange-50' : 
          'border-green-400 bg-green-50'
        }`}>
          <p className="text-xs font-bold text-gray-600 uppercase tracking-wider mb-3">💸 PRIME COST ACTUEL</p>
          <div className="grid grid-cols-3 gap-2 mb-3">
            <div className="text-center p-2 rounded-lg bg-white">
              <p className="text-[10px] text-gray-500">Food Cost</p>
              <p className="text-xl font-black text-gray-800">{analysis.primeCostData.foodCost}%</p>
            </div>
            <div className="text-center p-2 rounded-lg bg-white">
              <p className="text-[10px] text-gray-500">Masse Sal.</p>
              <p className="text-xl font-black text-gray-800">{analysis.primeCostData.masseSalariale}%</p>
            </div>
            <div className={`text-center p-2 rounded-lg ${
              analysis.primeCostData.isCritical ? 'bg-red-200' : 
              analysis.primeCostData.isWarning ? 'bg-orange-200' : 'bg-green-200'
            }`}>
              <p className="text-[10px] text-gray-600">TOTAL</p>
              <p className={`text-xl font-black ${
                analysis.primeCostData.isCritical ? 'text-red-700' : 
                analysis.primeCostData.isWarning ? 'text-orange-700' : 'text-green-700'
              }`}>{analysis.primeCostData.total}%</p>
            </div>
          </div>
          <div className="flex items-center justify-between p-2 rounded-lg bg-white">
            <span className="text-sm font-bold text-gray-600">🎯 Objectif :</span>
            <span className="text-sm font-black text-green-600">55-60%</span>
          </div>
          {analysis.primeCostData.isWarning && (
            <p className="text-xs text-red-600 font-bold mt-2 text-center">
              ⚠️ Prime Cost trop élevé = marge compressée
            </p>
          )}
        </div>
      )}
      
      {/* 3. PLAN D'ACTION PRSNL (AUTO-GÉNÉRÉ) */}
      {analysis.actionPlan.length > 0 && (
        <div className="p-4 rounded-xl bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-300">
          <p className="text-xs font-bold text-green-700 uppercase tracking-wider mb-3">💼 PLAN D'ACTION PRSNL (AUTO-GÉNÉRÉ)</p>
          <div className="space-y-2 mb-4">
            {analysis.actionPlan.map((action, idx) => (
              <div key={idx} className="flex items-center justify-between p-2 rounded-lg bg-white">
                <div className="flex items-center gap-2">
                  <span className="text-lg">{action.icon}</span>
                  <div>
                    <p className="font-bold text-sm text-gray-800">{action.label}</p>
                    <p className="text-xs text-gray-500">{action.detail}</p>
                  </div>
                </div>
                <span className="font-black text-green-600">+{action.gain.toLocaleString('fr-FR')}€/mois</span>
              </div>
            ))}
          </div>
          <div className="p-3 rounded-xl bg-green-600 text-white">
            <div className="flex items-center justify-between">
              <span className="font-bold">💥 TOTAL IMPACT :</span>
              <span className="text-2xl font-black">+{analysis.totalPotentialGain.toLocaleString('fr-FR')}€/mois</span>
            </div>
            <p className="text-sm mt-1 opacity-90">
              = <span className="font-bold text-yellow-300">+{(analysis.totalPotentialGain * 12).toLocaleString('fr-FR')}€/an</span> pour ton resto
            </p>
          </div>
        </div>
      )}
      
      {/* 4. PRIORITÉS D'ACTION */}
      {analysis.priorities.length > 0 && (
        <div className="p-4 rounded-xl bg-blue-50 border border-blue-200">
          <p className="text-xs font-bold text-blue-700 uppercase tracking-wider mb-3">🎯 PRIORITÉS (ordre d'impact)</p>
          <div className="space-y-2">
            {analysis.priorities.map((p, idx) => (
              <div key={idx} className="flex items-center gap-3">
                <span className={`w-7 h-7 rounded-full flex items-center justify-center font-black text-sm ${
                  idx === 0 ? 'bg-green-500 text-white' : 
                  idx < 3 ? 'bg-blue-500 text-white' : 'bg-gray-300 text-gray-700'
                }`}>{p.rank}</span>
                <span className="font-bold text-gray-800">{p.label}</span>
                <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                  p.type === 'quick win' ? 'bg-green-100 text-green-700' :
                  p.type === 'high impact' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                }`}>{p.type}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* 5. SCORE DE MATURITÉ */}
      <MaturityScore score={analysis.maturityScore} />
      
      {/* 6. ANGLES DE CLOSING */}
      <div className="p-4 rounded-xl bg-purple-50 border border-purple-200">
        <p className="text-xs font-bold text-purple-700 uppercase tracking-wider mb-3">🧲 ANGLES DE CLOSING</p>
        <div className="space-y-2">
          {analysis.closingAngles.map((angle, idx) => (
            <div key={idx} className="flex items-start gap-2 p-2 rounded-lg bg-white">
              <ArrowRight className="w-4 h-4 text-purple-500 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-gray-700">{angle}</p>
            </div>
          ))}
        </div>
      </div>
      
      {/* 7. OBJECTIONS ANTICIPÉES */}
      <div className="p-4 rounded-xl bg-red-50 border border-red-200">
        <button 
          onClick={() => setShowObjections(!showObjections)}
          className="w-full flex items-center justify-between"
        >
          <p className="text-xs font-bold text-red-700 uppercase tracking-wider">❌ OBJECTIONS POSSIBLES</p>
          {showObjections ? <ChevronUp className="w-5 h-5 text-red-500" /> : <ChevronDown className="w-5 h-5 text-red-500" />}
        </button>
        
        <AnimatePresence>
          {showObjections && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <div className="space-y-3 mt-3">
                {analysis.objections.map((obj, idx) => (
                  <div key={idx} className="p-3 rounded-lg bg-white border border-red-100">
                    <p className="text-sm font-bold text-red-600 mb-2">❌ "{obj.objection}"</p>
                    <div className="p-2 rounded bg-green-50 border border-green-200">
                      <p className="text-xs font-bold text-green-700 mb-1">✅ Réponse :</p>
                      <p className="text-sm text-green-800">{obj.response}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      
      {/* 8. RECOMMANDATIONS IA */}
      <div className="p-4 rounded-xl bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-200">
        <p className="text-xs font-bold text-indigo-700 uppercase tracking-wider mb-3">🤖 RECOMMANDATIONS IA</p>
        <div className="space-y-2">
          {analysis.aiRecommendations.map((rec, idx) => (
            <div key={idx} className="flex items-start gap-2 p-2 rounded-lg bg-white">
              <span className="text-lg">{rec.icon}</span>
              <p className="text-sm text-gray-700">{rec.text}</p>
            </div>
          ))}
        </div>
      </div>
      
      {/* 9. PSYCHOLOGIE DU LEAD (remplace "Pourquoi il va acheter") */}
      <div className="p-4 rounded-xl bg-amber-50 border border-amber-200">
        <p className="text-xs font-bold text-amber-700 uppercase tracking-wider mb-3">🧠 PSYCHOLOGIE DU LEAD</p>
        <div className="space-y-3">
          <div>
            <label className="text-[10px] text-gray-500 uppercase">Douleur principale</label>
            <input
              type="text"
              value={psychology.douleur}
              onChange={(e) => handlePsychologyChange('douleur', e.target.value)}
              placeholder="Ex: Perd de l'argent, épuisé, pas de vie perso..."
              className="w-full px-3 py-2 rounded-lg border border-amber-200 text-sm focus:outline-none focus:border-amber-400"
            />
          </div>
          <div>
            <label className="text-[10px] text-gray-500 uppercase">Objectif</label>
            <input
              type="text"
              value={psychology.objectif}
              onChange={(e) => handlePsychologyChange('objectif', e.target.value)}
              placeholder="Ex: Gagner plus, avoir du temps libre, ouvrir un 2ème..."
              className="w-full px-3 py-2 rounded-lg border border-amber-200 text-sm focus:outline-none focus:border-amber-400"
            />
          </div>
          <div>
            <label className="text-[10px] text-gray-500 uppercase">Niveau d'urgence</label>
            <select
              value={psychology.urgence}
              onChange={(e) => handlePsychologyChange('urgence', e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-amber-200 text-sm focus:outline-none focus:border-amber-400"
            >
              <option value="low">🟢 Faible - Explore les options</option>
              <option value="medium">🟡 Moyen - Cherche des solutions</option>
              <option value="high">🟠 Élevé - Prêt à agir</option>
              <option value="critical">🔴 Critique - Doit agir maintenant</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] text-gray-500 uppercase">Budget estimé</label>
            <input
              type="text"
              value={psychology.budget}
              onChange={(e) => handlePsychologyChange('budget', e.target.value)}
              placeholder="Ex: OK pour 5k€, doit réfléchir, demande facilités..."
              className="w-full px-3 py-2 rounded-lg border border-amber-200 text-sm focus:outline-none focus:border-amber-400"
            />
          </div>
          <div>
            <label className="text-[10px] text-gray-500 uppercase">Motivation</label>
            <input
              type="text"
              value={psychology.motivation}
              onChange={(e) => handlePsychologyChange('motivation', e.target.value)}
              placeholder="Ex: Famille, liberté, argent, reconnaissance..."
              className="w-full px-3 py-2 rounded-lg border border-amber-200 text-sm focus:outline-none focus:border-amber-400"
            />
          </div>
        </div>
      </div>
      
      {/* 10. PHRASE DE CLOSING FINALE */}
      <div className="p-4 rounded-xl bg-gradient-to-r from-gray-900 to-gray-800 text-white text-center">
        <p className="text-lg font-black mb-2">
          "On ne te vend pas du conseil.
        </p>
        <p className="text-lg font-black text-orange-400">
          On t'implémente un système qui te fait gagner de l'argent."
        </p>
      </div>
    </div>
  );
};

export default SalesAnalysisTable;
